import { FormApi } from "final-form";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "src/ui/shared/CustomButton";
import { Modal } from "src/ui/shared/Modal/Modal";
import { showSuccessToast } from "src/ui/shared/toasts/Toasts";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";

import {
    useFetchAllProductsQuery,
    useUpdateProductAndroidVersionMutation,
    useUpdateProductCodenameMutation,
    useUpdateProductMutation,
} from "@dashboard/products/api";
import { DialogProps, useTheme } from "@mui/material";
import { TextField } from "@shared/form/TextField";

const validationSchema = z.object({
    codename: z.string(),
    version: z.coerce.number(),
    name: z.string(),
});

const useStyles = makeStyles()((theme) => ({
    dialog: {
        minWidth: 400,
        gap: theme.spacing(1),
        display: "flex",
        flexDirection: "column",
    },
    errorMessage: {
        color: "red",
        fontSize: "12px",
        padding: theme.spacing(1),
    },
}));

type FormValues = {
    codename: string;
    version: string;
    duplicate: boolean;
    name: string;
};

type Props = DialogProps & {
    product: API.Product;
    onClose: () => void;
};

export const EditProductDialog = (props: Props) => {
    const { classes } = useStyles();
    const theme = useTheme();
    const { t } = useTranslation(["modals", "common"]);

    const [updateProductAndroidVersion] = useUpdateProductAndroidVersionMutation();
    const [updateProductCodename] = useUpdateProductCodenameMutation();
    const [updateProduct] = useUpdateProductMutation();

    const { data: products = [] } = useFetchAllProductsQuery();

    const otherProducts = products.filter((item) => item.id !== props.product.id);

    const onValidate = async (values: FormValues) => {
        const errors: Partial<Record<keyof FormValues, string>> = {};

        const isDuplicate = otherProducts.some(
            (item) => item.codename === values.codename && item.androidVersion === values.version,
        );
        if (isDuplicate) {
            errors.duplicate = t("modals:editProduct.productDuplicateError");
        }

        const result = validationSchema.safeParse(values);
        if (!result.success) {
            result.error.issues.forEach((issue) => {
                if (issue.path.length > 0) {
                    errors[issue.path[0] as keyof FormValues] = issue.message;
                }
            });
        }
        return errors;
    };
    const onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
        const updatedProduct = { ...props.product };

        if (form.getFieldState("version")?.dirty) {
            const result = await updateProductAndroidVersion({ productId: props.product.id, newVersion: values.version });
            if ("error" in result) {
                props.onClose();
                return;
            }
            updatedProduct.androidVersion = values.version;
        }
        if (form.getFieldState("codename")?.dirty) {
            const result = await updateProductCodename({ productId: props.product.id, newCodename: values.codename });
            if ("error" in result) {
                props.onClose();
                return;
            }
            updatedProduct.codename = values.codename;
        }
        if (form.getFieldState("name")?.dirty && updatedProduct.customBuildConfig) {
            const result = await updateProduct({
                ...updatedProduct,
                customBuildConfig: { ...updatedProduct.customBuildConfig, productName: values.name },
            });
            if ("error" in result) {
                props.onClose();
                return;
            }
        }

        showSuccessToast(t("modals:editProduct.toast.productUpdatedSuccess"));
        props.onClose();
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={onValidate}
            initialValues={{
                codename: props.product.codename,
                version: props.product.androidVersion,
                name: props.product.customBuildConfig?.productName,
            }}
            render={({ handleSubmit, submitting, form, errors }) => (
                <form onSubmit={handleSubmit} id="editProduct">
                    <Modal
                        open={props.open}
                        onClose={props.onClose}
                        title={props.title}
                        endButton={
                            <PrimaryButton type="submit" disabled={submitting || form.getState().pristine} form="editProduct">
                                {t("common:button.save")}
                            </PrimaryButton>
                        }
                    >
                        <TextField
                            fullWidth
                            label={t("modals:editProduct.productName")}
                            name="name"
                            style={{ marginTop: theme.spacing(1) }}
                        />
                        <TextField fullWidth label={t("modals:editProduct.codename")} name="codename" />
                        <TextField fullWidth label={t("modals:editProduct.androidVersion")} name="version" />
                        {errors?.duplicate && <div className={classes.errorMessage}>{errors.duplicate}</div>}
                    </Modal>
                </form>
            )}
        />
    );
};
